<template>
  <v-toolbar class="transparent elevation-0" extension-height="50px">
    <v-toolbar-title>Content Explorer</v-toolbar-title>
    <v-spacer></v-spacer>
    <search-bar />

    <template v-slot:extension>
      <breadcrumb class="explorer-content-breadcrumb" />
      <v-spacer></v-spacer>

      <template v-if="canShowContentExplorerButtons">
        <v-spacer></v-spacer>
        <content-type-filter-icon />
        <v-divider vertical class="mx-2"></v-divider>
        <content-view-toggle />

        <template v-if="canShowGlobalOptions">
          <v-divider vertical class="mx-2"></v-divider>
          <span id="global-options">
            <tool-new-folder v-if="canShowManageFoldersButtons()" />
            <tool-new-content v-if="canShowCreateContentButton()" tooltip="Create content in current path" />
            <toolbar-button
              v-if="canShowBulkUploadButton()"
              icon="mdi-upload"
              tooltip="Bulk upload new content"
              @click="openBulkUploadSelector"></toolbar-button>
          </span>
        </template>

        <v-divider vertical class="mx-2" v-if="canShowSelectedItemOptions"></v-divider>
        <span id="folder-options" v-if="isAllSelectedFolders && canShowManageFoldersButtons()">
          <tool-rename-folder />
          <tool-delete-folder />
        </span>
        <template v-if="canShowCreateRevisionButtons()">
          <span id="all-content-options" v-if="isAllSelectedContents && !isAnyDraftSelected">
            <tool-new-revision />
          </span>
          <span id="same-type-content-options" v-if="isAllSelectedSameType && !isAnyDraftSelected">
            <tool-bulk-change />
          </span>
        </template>
        <span id="global-selected-options" v-if="canShowManageFoldersButtons()">
          <tool-move-to />
        </span>
      </template>
    </template>
  </v-toolbar>
</template>

<script>
import { mapState } from "vuex"
import { PToolbar } from "plant-common"
import ToolNewRevision from "./Tools/NewRevision"
import ToolBulkChange from "./Tools/BulkChange"
import ToolNewFolder from "./Tools/NewFolder"
import ToolNewContent from "./Tools/NewContent"
import ToolRenameFolder from "./Tools/RenameFolder"
import ToolMoveTo from "./Tools/MoveTo"
import ContentTypeFilterIcon from "./Tools/ContentTypeFilterIcon.vue"
import SearchBar from "./Tools/SearchBar.vue"
import ToolDeleteFolder from "./Tools/DeleteFolder"
import ToolbarButton from "./Tools/ToolbarButton"

import ContentViewToggle from "./Tools/ContentViewToggle"
import BulkUpload from "./BulkUpload/index.vue"
import TypeDialog from "@/pages/workflow/components/new-content/TypeDialog.vue"
import { PtModal } from "plant-common"
import { Extensions } from "plant-common"

import Breadcrumb from "./Tools/Breadcrumb"

import { STORE_EXPLORER_NAME, STATE_FOLDER, STATE_SELECTED } from "@/store_constants/explorer"
import { STATE_PERMISSIONS_BY_CATEGORY, STORE_PERMISSIONS_NAME } from "@/store_constants/permissions"
import { checkUserPermissions } from "@/utils/userPermissionsControl"
import { getAllContentBodyTypes } from '@/classes/content/contentBodyFunctions';
export default {
  mixins: [PtModal],
  components: {
    Breadcrumb,
    ToolbarButton,
    ToolNewFolder,
    ToolNewContent,
    ToolRenameFolder,
    ToolDeleteFolder,
    ToolMoveTo,
    ToolNewRevision,
    ToolBulkChange,
    PToolbar,

    ContentTypeFilterIcon,
    SearchBar,
    BulkUpload,
    TypeDialog,
    ContentViewToggle
  },
  data() {
    return {
      bulkupload_dialog: false
    }
  },
  computed: {
    ...mapState(STORE_EXPLORER_NAME, {
      selected: STATE_SELECTED,
      folder: STATE_FOLDER
    }),
    ...mapState(STORE_PERMISSIONS_NAME, {
      permissionsByCategory: STATE_PERMISSIONS_BY_CATEGORY
    }),
    isAllSelectedContents() {
      if (!this.selected?.length > 0) return false
      
      let contents = []
      this.selected.forEach(e => e.header && contents.push(e))
      
      return contents.length === this.selected.length
    },
    isAllSelectedSameType() {
      if (!this.selected?.length > 0) return false
      
      let contents = []
      let content_type = false
      if (this.selected[0].header) {
        content_type = this.selected[0].header.metadata.content_revision_type
      }
      if (!content_type) return false
      
      this.selected.forEach(
        e => e.header && e.header.metadata.content_revision_type === content_type && contents.push(e)
      )
      
      return contents.length === this.selected.length
    },
    isAllSelectedFolders() {
      if (!this.selected?.length > 0) return false
      
      let folders = []
      this.selected.forEach(e => !e.header && folders.push(e))

      return folders.length === this.selected.length
    },
    isAnyDraftSelected() {
      return this.selected.some(item => item.state === "draft")
    },
    canShowContentExplorerButtons() {
      return (
        this.canShowCreateContentButton() ||
        this.canShowCreateRevisionButtons() ||
        this.canShowManageFoldersButtons() || 
        this.canShowBulkUploadButton()
      )
    },
    canShowGlobalOptions() {
      return (
        this.canShowCreateContentButton() ||
        this.canShowManageFoldersButtons() || 
        this.canShowBulkUploadButton()
      )
    },
    canShowSelectedItemOptions() {
      const hasSelectedItems = this.selected.length > 0
      const allApprovedContent = this.isAllSelectedContents && !this.isAnyDraftSelected
      const canManageFolders = this.canShowManageFoldersButtons()
      const canCreateRevision = this.canShowCreateRevisionButtons() && allApprovedContent

      return hasSelectedItems && (canManageFolders || canCreateRevision);
    }
  },
  methods: {
		async openBulkUploadSelector() {
      const bulk_upload_types = getAllContentBodyTypes();
      const availableContentTypes = Object.values(Extensions).filter(extension => {
        return bulk_upload_types.some(available => available === extension.name)
      })
      var type = await this.$modal(TypeDialog, {
        content_types: availableContentTypes
      })
      await this.$modal(BulkUpload, {
        value: true,
        content_type: type.name
      })
    },
    canShowCreateContentButton() {
      const permissions = [this.permissionsByCategory.contentManagement.createContent]
      
      return checkUserPermissions(permissions)
    },
    canShowCreateRevisionButtons() {
      const permissions = [this.permissionsByCategory.contentManagement.createRevisions]
      
      return checkUserPermissions(permissions)
    },
    canShowManageFoldersButtons() {
      const permissions = [this.permissionsByCategory.contentManagement.createContent]
      
      return checkUserPermissions(permissions)
    },
    canShowBulkUploadButton() {
      const permissions = [this.permissionsByCategory.contentManagement.bulkUpload]

      return checkUserPermissions(permissions)
    }
  }
}
</script>
